import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { varinymAPI } from '../lib/varinym-api';
import type { Context } from '../types';

const TABS = [
  { path: '/bulk', label: 'CSV Create Identities' },
  { path: '/bulk/replace', label: 'CSV Replace' },
  { path: '/bulk/json', label: 'JSON Bulk' },
] as const;

export function BulkOperations() {
  const location = useLocation();

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Bulk Operations</h1>

      {/* Tabs */}
      <div className="border-b border-border mb-6 overflow-x-auto overflow-y-hidden">
        <nav className="-mb-px flex space-x-8 min-w-max">
          {TABS.map((tab) => (
            <Link
              key={tab.path}
              to={tab.path}
              className={`link-bare ${
                location.pathname === tab.path
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {tab.label}
            </Link>
          ))}
          <Link
            to="/bulk/jobs"
            className="link-bare border-transparent text-muted-foreground hover:text-foreground hover:border-border whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors"
          >
            Job History
          </Link>
        </nav>
      </div>

      {location.pathname === '/bulk/replace' && <TransformUpload />}
      {location.pathname === '/bulk/json' && <JSONBulk />}
      {location.pathname !== '/bulk/replace' && location.pathname !== '/bulk/json' && <CSVUpload />}
    </div>
  );
}

function CSVUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const navigate = useNavigate();

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(true),
  });

  // Auto-select 'default' context when contexts data loads
  useEffect(() => {
    if (contextsData?.contexts) {
      const defaultContext = contextsData.contexts.find((s: Context) => s.name.toLowerCase() === 'default');
      if (defaultContext && !selectedContexts.includes(defaultContext.name)) {
        setSelectedContexts([defaultContext.name]);
      }
    }
  }, [contextsData]);

  const uploadMutation = useMutation({
    mutationFn: async ({ file, contexts }: { file: File; contexts: string[] }) => {
      if (contexts.length === 0) {
        throw new Error('At least one context must be selected');
      }

      // The API takes all selected contexts (comma-separated) in a single job
      return varinymAPI.uploadCSV(file, contexts);
    },
    onSuccess: (data) => {
      alert('CSV uploaded successfully!');
      navigate(`/bulk/jobs/${data.job_id}`);
    },
    onError: (err: Error) => {
      alert(`Upload failed: ${err.message}`);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      if (selectedFile.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file && selectedContexts.length > 0) {
      uploadMutation.mutate({ file, contexts: selectedContexts });
    }
  };

  const toggleContext = (contextName: string) => {
    const isDefault = contextName.toLowerCase() === 'default';

    // Prevent deselecting 'default' context
    if (isDefault && selectedContexts.includes(contextName)) {
      return;
    }

    // Toggle context selection
    setSelectedContexts(prev =>
      prev.includes(contextName)
        ? prev.filter(s => s !== contextName)
        : [...prev, contextName]
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card title="Upload CSV File">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:brightness-105"
                  required
                />
                {file && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contexts (select one or more)
                </label>
                <div className="flex flex-wrap gap-2">
                  {contextsData?.contexts?.map((s: Context) => {
                    const isSelected = selectedContexts.includes(s.name);
                    const isDefault = s.name.toLowerCase() === 'default';
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleContext(s.name)}
                        disabled={isDefault && isSelected}
                        className={`px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-muted text-foreground flex items-center gap-2'
                            : 'border-border bg-card text-foreground hover:border-border hover:bg-muted'
                        } ${isDefault && isSelected ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {isSelected && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px]">✓</span>
                        )}
                        {s.name}
                        {isDefault && isSelected && (
                          <span className="ml-1 text-xs">(required)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {contextsData?.contexts && contextsData.contexts.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">No contexts available. Create a context first.</p>
                )}
                {selectedContexts.length > 0 && (
                  <div className="mt-3 bg-muted border border-border rounded-md p-3 flex gap-2 items-start">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      <strong>Note:</strong> Aliases will be automatically created for {selectedContexts.length} context{selectedContexts.length > 1 ? 's' : ''}: <strong>{selectedContexts.join(', ')}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="mt-6 w-full"
              isLoading={uploadMutation.isPending}
              disabled={!file || selectedContexts.length === 0}
            >
              Upload and Process
            </Button>
          </form>
        </Card>
      </div>

      <div>
        <Card title="CSV Format">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>Your CSV file must have a header row with:</p>
            <pre className="bg-muted p-2 rounded border border-border font-mono text-xs">
              user_id
            </pre>
            <p className="font-medium">Example:</p>
            <pre className="bg-muted p-2 rounded border border-border font-mono text-xs">
              {`user_id
user1@example.com
user2@example.com
user3@example.com`}
            </pre>
            <div className="pt-3 border-t border-border">
              <p className="font-medium mb-2">Limits:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Max file size: 50MB</li>
                <li>Max rows: 10,000</li>
                <li>Processing timeout: 25 seconds</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// Splits a single CSV header line on commas, respecting double-quoted fields
// (including "" as an escaped quote). Good enough for a header preview — the
// server is still the source of truth on whether the chosen column exists.
function parseCsvHeaderLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields.filter(Boolean);
}

function TransformUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [operation, setOperation] = useState<'replace' | 'resolve'>('replace');
  const [column, setColumn] = useState('');
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [context, setContext] = useState('');
  const [onFailure, setOnFailure] = useState<'error_message' | 'keep_original'>('error_message');
  const navigate = useNavigate();

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(true),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Select a CSV file');
      if (!column.trim()) throw new Error('Column name is required');
      if (operation === 'replace' && !context) throw new Error('Context is required for replace');

      return varinymAPI.uploadTransform(file, {
        operation,
        column: column.trim(),
        context: operation === 'replace' ? context : undefined,
        onFailure,
      });
    },
    onSuccess: (data) => {
      navigate(`/bulk/jobs/${data.job_id}`);
    },
    onError: (err: Error) => {
      alert(`Upload failed: ${err.message}`);
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      if (selectedFile.size > 100 * 1024 * 1024) {
        alert('File size must be less than 100MB');
        return;
      }
      setFile(selectedFile);
      setDetectedColumns([]);

      // Peek at just the first chunk (not the whole file) to show the header
      // row so the user can pick the column instead of guessing its spelling.
      // This is separate from the upload itself, which still streams the
      // full File object untouched.
      try {
        const headerChunk = await selectedFile.slice(0, 64 * 1024).text();
        const firstLine = headerChunk.split(/\r\n|\r|\n/)[0] ?? '';
        setDetectedColumns(parseCsvHeaderLine(firstLine));
      } catch {
        // Non-fatal — the column name field still works via manual entry.
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    uploadMutation.mutate();
  };

  const canSubmit = !!file && !!column.trim() && (operation !== 'replace' || !!context);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card title="Transform CSV Column">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:brightness-105"
                  required
                />
                {file && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Operation
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOperation('replace')}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                      operation === 'replace'
                        ? 'border-primary bg-muted text-foreground'
                        : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    Protect
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                      User IDs → aliases (masking)
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOperation('resolve')}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                      operation === 'resolve'
                        ? 'border-primary bg-muted text-foreground'
                        : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    Resolve
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                      Aliases → real user IDs
                    </span>
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="transform-column" className="block text-sm font-medium text-foreground mb-1">
                  Column name
                </label>
                {detectedColumns.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {detectedColumns.map((col) => (
                      <button
                        key={col}
                        type="button"
                        onClick={() => setColumn(col)}
                        className={`px-3 py-1.5 rounded-md border-2 font-mono text-xs transition-colors ${
                          column === col
                            ? 'border-primary bg-muted text-foreground'
                            : 'border-border bg-card text-foreground hover:bg-muted'
                        }`}
                      >
                        {col}
                      </button>
                    ))}
                  </div>
                )}
                <input
                  id="transform-column"
                  type="text"
                  value={column}
                  onChange={(e) => setColumn(e.target.value)}
                  placeholder="user_id"
                  className="block w-full rounded-md border border-border-control bg-popover px-3 py-2 text-sm text-foreground focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 focus:ring-offset-0 transition-colors"
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {detectedColumns.length > 0
                    ? 'Detected from the file header — click one above, or type a different name.'
                    : 'Name of the CSV column to rewrite in place. Checked against the file after upload.'}
                </p>
              </div>

              {operation === 'replace' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Context
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {contextsData?.contexts?.map((s: Context) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setContext(s.name)}
                        className={`px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                          context === s.name
                            ? 'border-primary bg-muted text-foreground flex items-center gap-2'
                            : 'border-border bg-card text-foreground hover:border-border hover:bg-muted'
                        }`}
                      >
                        {context === s.name && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px]">✓</span>
                        )}
                        {s.name}
                      </button>
                    ))}
                  </div>
                  {contextsData?.contexts && contextsData.contexts.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2">No contexts available. Create a context first.</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  On failure
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOnFailure('error_message')}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors text-left ${
                      onFailure === 'error_message'
                        ? 'border-primary bg-muted text-foreground'
                        : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    Error message (recommended)
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                      Failed rows show <code>#ERROR: ...</code> instead of a value
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnFailure('keep_original')}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors text-left ${
                      onFailure === 'keep_original'
                        ? 'border-primary bg-muted text-foreground'
                        : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    Keep original
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                      Failed rows keep their original, unmasked value
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="mt-6 w-full"
              isLoading={uploadMutation.isPending}
              disabled={!canSubmit}
            >
              Upload and Analyze
            </Button>
          </form>
        </Card>
      </div>

      <div>
        <Card title="How Transform Works">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Upload a multi-column CSV and rewrite one named column in place.
              Every other column is left untouched.
            </p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Protect</strong> masks user IDs as aliases. Requires a context.</li>
              <li><strong>Resolve</strong> de-anonymises aliases back to real user IDs. Console-only — never available via API key.</li>
            </ul>
            <div className="pt-3 border-t border-border">
              <p className="font-medium mb-2">What happens next:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>File is analyzed — column existence and row count are checked.</li>
                <li>You'll see a before/after preview and confirm before anything runs.</li>
                <li>Processing runs, then the transformed CSV is available to download for 90 minutes.</li>
              </ol>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="font-medium mb-2">Limits:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Max 50,000 rows × 1,024 columns</li>
                <li>Under 100 MB</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function JSONBulk() {
  const [operation, setOperation] = useState<'identities' | 'aliases' | 'resolve'>('identities');
  const [jsonInput, setJsonInput] = useState('');
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const [result, setResult] = useState<any>(null);

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(true),
  });

  // 'default' is created automatically for every identity, so Add Aliases
  // never needs to offer it — there's nothing to add.
  const addableAliasContexts = (contextsData?.contexts ?? []).filter(
    (s: Context) => s.name.toLowerCase() !== 'default'
  );

  // Auto-select 'default' when switching to Create Identities, since every
  // identity gets it regardless — mirrors the Create Identity page. Add
  // Aliases gets no default selection; the admin picks explicitly.
  const handleOperationChange = (newOperation: 'identities' | 'aliases' | 'resolve') => {
    setOperation(newOperation);
    if (newOperation === 'identities' && contextsData?.contexts) {
      const defaultContext = contextsData.contexts.find(s => s.name.toLowerCase() === 'default');
      if (defaultContext && !selectedContexts.includes(defaultContext.name)) {
        setSelectedContexts([defaultContext.name]);
      }
    } else if (newOperation === 'aliases') {
      setSelectedContexts([]);
    }
  };

  const bulkMutation = useMutation({
    mutationFn: async () => {
      const data = JSON.parse(jsonInput);

      if (operation === 'identities') {
        if (selectedContexts.length === 0) {
          throw new Error('At least one context required for identities');
        }

        // Create identities with aliases for each selected context
        const results = await Promise.all(
          selectedContexts.map(context =>
            varinymAPI.bulkCreateIdentities(data, context)
          )
        );

        // Combine results
        return {
          total: results.reduce((sum, r) => sum + r.total, 0),
          successful: results.reduce((sum, r) => sum + r.successful, 0),
          failed: results.reduce((sum, r) => sum + r.failed, 0),
          results: results.flatMap(r => r.results),
          contexts: selectedContexts,
        };
      } else if (operation === 'aliases') {
        if (selectedContexts.length === 0) {
          throw new Error('At least one context required for aliases');
        }

        // Create aliases for each selected context
        const results = await Promise.all(
          selectedContexts.map(context =>
            varinymAPI.bulkCreateAliases(data, context)
          )
        );

        // Combine results
        return {
          total: results.reduce((sum, r) => sum + r.total, 0),
          successful: results.reduce((sum, r) => sum + r.successful, 0),
          failed: results.reduce((sum, r) => sum + r.failed, 0),
          results: results.flatMap(r => r.results),
          contexts: selectedContexts,
        };
      } else {
        return varinymAPI.bulkResolveAliases(data);
      }
    },
    onSuccess: (data) => {
      setResult(data);
      alert('Bulk operation completed!');
    },
    onError: (err: Error) => {
      alert(`Error: ${err.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = JSON.parse(jsonInput);
      if (!Array.isArray(data)) {
        alert('JSON must be an array');
        return;
      }
      if (data.length > 100) {
        alert('Maximum 100 items for JSON bulk operations');
        return;
      }
      bulkMutation.mutate();
    } catch (err) {
      alert('Invalid JSON format');
    }
  };

  const toggleContext = (contextName: string) => {
    const isDefault = contextName.toLowerCase() === 'default';

    // Prevent deselecting 'default' context
    if (isDefault && selectedContexts.includes(contextName)) {
      return;
    }

    // Toggle context selection
    setSelectedContexts(prev =>
      prev.includes(contextName)
        ? prev.filter(s => s !== contextName)
        : [...prev, contextName]
    );
  };

  return (
    <div className="space-y-6">
      <Card title="JSON Bulk Operations">
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Operation Type
              </label>
              <select
                value={operation}
                onChange={(e) => handleOperationChange(e.target.value as any)}
                className="block w-full rounded-md border border-border-control bg-popover px-3 py-2 text-sm text-foreground focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 focus:ring-offset-0 transition-colors"
              >
                <option value="identities">Create Identities</option>
                <option value="aliases">Add Aliases</option>
                <option value="resolve">Resolve Aliases</option>
              </select>
            </div>

            {(operation === 'identities' || operation === 'aliases') && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contexts (select one or more)
                </label>
                <div className="flex flex-wrap gap-2">
                  {(operation === 'aliases' ? addableAliasContexts : contextsData?.contexts ?? []).map((s: Context) => {
                    const isSelected = selectedContexts.includes(s.name);
                    const isDefault = s.name.toLowerCase() === 'default';
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleContext(s.name)}
                        disabled={isDefault && isSelected}
                        className={`px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-muted text-foreground flex items-center gap-2'
                            : 'border-border bg-card text-foreground hover:border-border hover:bg-muted'
                        } ${isDefault && isSelected ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {isSelected && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px]">✓</span>
                        )}
                        {s.name}
                        {isDefault && isSelected && (
                          <span className="ml-1 text-xs">(required)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {operation === 'aliases' && (
                  <p className="text-xs text-muted-foreground mt-2">
                    "default" isn't listed — every identity already has it automatically.
                  </p>
                )}
                {contextsData?.contexts && (operation === 'aliases' ? addableAliasContexts : contextsData.contexts).length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">No contexts available. Create a context first.</p>
                )}
                {selectedContexts.length > 0 && (
                  <div className="mt-3 bg-muted border border-border rounded-md p-3 flex gap-2 items-start">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      <strong>Note:</strong> Aliases will be created for {selectedContexts.length} context{selectedContexts.length > 1 ? 's' : ''}: <strong>{selectedContexts.join(', ')}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                JSON Data (max 100 items)
              </label>
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                rows={10}
                className="block w-full rounded-md border border-border-control bg-popover px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 focus:ring-offset-0 transition-colors"
                placeholder={
                  operation === 'resolve'
                    ? '["alias-id-1", "alias-id-2"]'
                    : '["user1@example.com", "user2@example.com"]'
                }
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="mt-6 w-full"
            isLoading={bulkMutation.isPending}
            disabled={!jsonInput.trim()}
          >
            Execute Bulk Operation
          </Button>
        </form>
      </Card>

      {result && (
        <Card title="Results">
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <div className="flex-1 bg-info/10 p-4 rounded">
                <div className="text-info font-medium">Total</div>
                <div className="text-2xl font-bold text-info">{result.total}</div>
              </div>
              <div className="flex-1 bg-success/10 p-4 rounded">
                <div className="text-success font-medium">Successful</div>
                <div className="text-2xl font-bold text-success">{result.successful}</div>
              </div>
              <div className="flex-1 bg-destructive/10 p-4 rounded">
                <div className="text-destructive font-medium">Failed</div>
                <div className="text-2xl font-bold text-destructive">{result.failed}</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <pre className="bg-muted p-4 rounded border border-border text-xs overflow-auto max-h-96">
                {JSON.stringify(result.results, null, 2)}
              </pre>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
